import type { TFunction } from "i18next";

/**
 * Clean, human-readable labels for raw product attribute keys. Shared by the
 * catalog product page and product cards so both show the same spec names
 * (e.g. `protein` → "Protein") instead of raw/abbreviated keys. Doubles as the
 * English fallback source for the `attr.<key>` i18n keys.
 */
export const ATTR_LABELS: Record<string, string> = {
  code: "Article code",
  protein: "Protein",
  fat: "Fat",
  moisture: "Moisture",
  industry: "Industry",
};

export function prettyAttr(key: string, t: TFunction): string {
  const fallback =
    ATTR_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return t("attr." + key, fallback);
}

/**
 * Human-readable label for an `industry` attribute slug (e.g. `meat-industry`
 * → "Mesna industrija"). The soy catalog carries suitable industries as a
 * comma-joined list of slugs on each product (see DemoCatalogSeeder); this
 * resolves one slug through the `industry.<slug>` i18n keys, falling back to
 * English-derived slug prettification for slugs without a translation.
 */
export function prettyIndustry(slug: string, t: TFunction): string {
  const key = slug.trim();
  const s = key.replace(/-/g, " ");
  const fallback = s.charAt(0).toUpperCase() + s.slice(1);
  return t("industry." + key, fallback);
}

/**
 * Whether the SKU is an article code rather than a name. Articles from the ERP carry their numeric
 * code as SKU and the trade name in the product name ("BIOPRO 10 - …"), so the name leads; the
 * bundled demo catalog carries the trade name in the SKU itself ("BIOPRO 10"), so the SKU leads.
 */
export function skuIsCode(sku: string): boolean {
  return /^[\d./-]+$/.test(sku.trim());
}

/**
 * The products the catalog lists: all of them except an ERP article that is a packaging of a catalog
 * product, because a packaging is chosen and ordered on its product's page. Any other ERP article is a
 * product of its own (a premix, a feed, soybean meal…) and is listed as one.
 */
export function listedProducts<T extends { sku: string; fromErp?: boolean; attributes?: Record<string, unknown> | null }>(
  all: T[],
): T[] {
  const packagings = new Set(all.filter((p) => !p.fromErp).flatMap(packagingCodes));
  return all.filter((p) => !p.fromErp || !packagings.has(p.sku));
}

/**
 * The ERP article codes a catalog product is ordered as (its `pakovanja` attribute). The ERP takes an order
 * line only with one of its own articles, so a catalog product is ordered as one of its packagings.
 */
export function packagingCodes(p: { attributes?: Record<string, unknown> | null } | null | undefined): string[] {
  const v = p?.attributes?.pakovanja;
  return Array.isArray(v) ? v.map(String).filter(Boolean) : [];
}

/** Split the comma-joined raw `industry` attribute value into individual slugs. */
export function splitIndustry(raw: string): string[] {
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}
